import { z } from "zod";
import { apiClient } from "./api";
import { ProviderName } from "./model-settings";

const providerSchema = z.enum(ProviderName);

export const chatModelTierSchema = z.enum(["simple", "standard", "complex"]);
export type ChatModelTier = z.infer<typeof chatModelTierSchema>;

// Admin-facing row shape. Mirrors `ChatAllowedModelAdminResponse` in
// `backend/src/api/settings_chat_allowed.py`.
export const chatAllowedAdminSchema = z.object({
  provider: providerSchema,
  model_id: z.string(),
  display_name: z.string().nullable(),
  is_default: z.boolean(),
  tier: chatModelTierSchema.nullable(),
  capability_flags: z.object({
    accepts_reasoning_effort: z.boolean(),
    accepts_minimal_effort: z.boolean(),
    accepts_extended_thinking: z.boolean(),
    accepts_structured_outputs: z.boolean(),
    reasoning_not_disableable: z.boolean(),
  }),
  updated_at: z.string().nullable(),
  updated_by: z.string().nullable(),
});
export type ChatAllowedAdminRow = z.infer<typeof chatAllowedAdminSchema>;

// Lean read shape consumed by the chat dropdown. Mirrors
// `ChatAllowedModelForUserResponse`.
export const chatAllowedForUserSchema = z.object({
  provider: providerSchema,
  model_id: z.string(),
  display_name: z.string().nullable(),
  is_default: z.boolean(),
  tier: chatModelTierSchema.nullable(),
});
export type ChatAllowedForUser = z.infer<typeof chatAllowedForUserSchema>;

const chatAllowedAdminListSchema = z.array(chatAllowedAdminSchema);
const chatAllowedForUserListSchema = z.array(chatAllowedForUserSchema);

// Encode `model_id` for paths because OpenRouter mirrors contain
// slashes (e.g. `anthropic/claude-3.5-sonnet`). The backend route uses
// `model_id:path` so the slash isn't intercepted as a segment
// separator, but we still URL-encode characters that would confuse
// browsers / proxies.
function encodePath(modelId: string): string {
  return modelId.split("/").map(encodeURIComponent).join("/");
}

export const chatAllowedModelsApi = {
  listAdmin: () => apiClient.get("/settings/chat/allowed-models", chatAllowedAdminListSchema),
  listForChat: () => apiClient.get("/chat/allowed-models", chatAllowedForUserListSchema),
  add: (body: { provider: ProviderName; model_id: string }) =>
    apiClient.post("/settings/chat/allowed-models", chatAllowedAdminSchema, body),
  remove: (provider: ProviderName, modelId: string) =>
    apiClient.delete(`/settings/chat/allowed-models/${provider}/${encodePath(modelId)}`, z.void()),
  setDefault: (provider: ProviderName, modelId: string) =>
    apiClient.put(
      `/settings/chat/allowed-models/${provider}/${encodePath(modelId)}/default`,
      chatAllowedAdminListSchema,
      // Endpoint takes no body — the path carries the target — but the
      // apiClient.put helper requires a body argument. Empty object is
      // serialised to `{}`; FastAPI ignores it for routes without a
      // declared body model.
      {},
    ),
  setTier: (provider: ProviderName, modelId: string, tier: ChatModelTier | null) =>
    apiClient.put(
      `/settings/chat/allowed-models/${provider}/${encodePath(modelId)}/tier`,
      chatAllowedAdminListSchema,
      { tier },
    ),
};

// Error code shapes surfaced by the API. The mutations consume these to
// render actionable feedback (e.g. "rotate the default first" instead of
// the raw 409 status code).
export const cannotDeleteDefaultDetail = z.object({
  code: z.literal("cannot_delete_default"),
  provider: providerSchema,
  model_id: z.string(),
});
export const cannotDeleteLastAllowedDetail = z.object({
  code: z.literal("cannot_delete_last_allowed"),
  provider: providerSchema,
  model_id: z.string(),
});
export const alreadyAllowedDetail = z.object({
  code: z.literal("already_allowed"),
  provider: providerSchema,
  model_id: z.string(),
});
export const notInAllowlistDetail = z.object({
  code: z.literal("not_in_allowlist"),
  provider: providerSchema,
  model_id: z.string(),
});
export const tierTakenDetailSchema = z.object({
  code: z.literal("tier_taken"),
  tier: chatModelTierSchema,
  holder_provider: providerSchema,
  holder_model_id: z.string(),
});
export type TierTakenDetail = z.infer<typeof tierTakenDetailSchema>;

export const CHAT_ALLOWED_QUERY_KEY = ["settings", "chat", "allowed-models"] as const;
export const CHAT_ALLOWED_FOR_USER_QUERY_KEY = ["chat", "allowed-models"] as const;
